import { ArrowLeftIcon, ArrowRightIcon, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { UseFormReturn } from 'react-hook-form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import type { WaitingRoomPage } from '@/features/webinar/types';
import IconFlash from '@/components/icons/IconFlash';
import { useEffect, useState } from 'react';

type QuizFormValues = {
  name: string;
  subtitle: string;
  questions: {
    id?: number;
    textContent: string;
    type: 'SINGLE_CHOICE';
    options: {
      textContent: string;
      correct: boolean;
      position?: number | null;
    }[];
  }[];
};

interface PreviewActivityQuizProps {
  form: UseFormReturn<QuizFormValues>;
  visualIdentity: WorkspaceVisualIdentity;
  currentWaitingRoomPage?: WaitingRoomPage | null;
}

export default function PreviewActivityQuiz({
  form,
  visualIdentity,
  currentWaitingRoomPage,
}: PreviewActivityQuizProps) {
  const theme = visualIdentity?.theme;
  const { t } = useTranslation('webinars');
  const [quizStep, setQuizStep] = useState(1);
  const [selectedAnswer, setSelectedAnswer] = useState<{
    [questionIndex: number]: number;
  }>({});

  const gradientOne = `linear-gradient(to bottom, ${visualIdentity?.gradient1Color1 || '#000'}, ${visualIdentity?.gradient1Color2 || '#fff'})`;
  const gradientTwo = `linear-gradient(226deg, ${visualIdentity?.gradient2Color1 || '#000'} 0.2%, ${visualIdentity?.gradient2Color2 || '#fff'} 104.63%)`;
  const normalizedBackgroundImage =
    currentWaitingRoomPage?.backgroundImageUrl &&
    currentWaitingRoomPage.backgroundImageUrl.trim().length > 0
      ? decodeURIComponent(currentWaitingRoomPage.backgroundImageUrl)
      : undefined;

  const getPreviewBackground = () => {
    if (!currentWaitingRoomPage) {
      return visualIdentity?.backgroundColor || '#FBFBFB';
    }

    const { background, backgroundGradient } = currentWaitingRoomPage;

    if (background === 'GRADIENT') {
      return backgroundGradient === '2' ? gradientTwo : gradientOne;
    }

    return visualIdentity?.backgroundColor || '#FBFBFB';
  };

  const previewBackground = getPreviewBackground();
  const previewBackgroundStyle =
    currentWaitingRoomPage?.background === 'IMAGE' && normalizedBackgroundImage
      ? {
          backgroundImage: `url(${normalizedBackgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }
      : {
          background: previewBackground,
        };

  const questions = form.watch('questions') || [];
  const currentQuestion = questions[quizStep - 1];

  useEffect(() => {
    if (quizStep > questions.length && questions.length > 0) {
      setQuizStep(questions.length);
    }
  }, [questions.length, quizStep]);

  return (
    <div className="flex flex-col gap-4">
      <div
        className=" w-full rounded-[20px] p-4 max-w-[634px] mx-auto "
        style={previewBackgroundStyle}
      >
        {/* btn */}
        <div className="w-full grid grid-cols-3 gap-6 ">
          <div
            className="flex items-center gap-2 p-2 rounded-lg"
            style={{
              backgroundColor:
                theme === 'LIGHT' ? 'rgba(31, 31, 31, 0.03)' : 'rgba(255, 255, 255, 0.1)',
            }}
          >
            <div>
              <div className="size-10 rounded-lg bg-black flex items-center justify-center">
                <img src="/assets/connexion.svg" alt="connexion" />
              </div>
            </div>

            <div className="flex flex-col">
              <p
                className="text-xs"
                style={{
                  color: theme === 'DARK' ? 'white' : 'black',
                  fontFamily: visualIdentity?.fontFamily || 'Poppins',
                }}
              >
                {t('waitingRoom.survey.connectionQuality')}
              </p>
              <p
                className="text-sm"
                style={{
                  fontFamily: visualIdentity?.fontFamily || 'Poppins',
                  color: theme === 'DARK' ? 'white' : 'black',
                }}
              >
                {t('waitingRoom.survey.excellent')}
              </p>
            </div>
          </div>

          <div
            className="flex items-center gap-2 p-2 rounded-lg relative cursor-pointer "
            style={{
              backgroundColor:
                theme === 'LIGHT' ? 'rgba(31, 31, 31, 0.03)' : 'rgba(255, 255, 255, 0.1)',
              borderRadius: '10px',

              justifyContent: 'flex-start',
            }}
          >
            <div>
              <div className="size-10 rounded-lg bg-black flex items-center justify-center">
                <IconFlash color={visualIdentity?.contrastColor || '#000'} size={24} />
              </div>
            </div>
            <p
              className="text-sm"
              style={{
                fontFamily: visualIdentity?.fontFamily || 'Poppins',
                color: theme === 'DARK' ? 'white' : 'black',
              }}
              dangerouslySetInnerHTML={{
                __html: t('waitingRoom.survey.exploreTool'),
              }}
            />
          </div>

          <div
            className="flex items-center gap-2 p-2 rounded-lg relative cursor-pointer"
            style={{
              backgroundColor:
                theme === 'LIGHT' ? 'rgba(31, 31, 31, 0.03)' : 'rgba(255, 255, 255, 0.1)',
              borderRadius: '10px 10px 0 0',
              justifyContent: 'center',
            }}
          >
            <div className="flex  gap-2">
              <p
                className="text-sm text-center"
                style={{
                  fontFamily: visualIdentity?.fontFamily || 'Poppins',
                  color: theme === 'DARK' ? 'white' : 'black',
                }}
                dangerouslySetInnerHTML={{
                  __html: t('waitingRoom.survey.doSurvey'),
                }}
              />
              <X
                className="size-6  "
                style={{
                  color: theme === 'DARK' ? 'white' : 'black',
                }}
              />
            </div>

            <div
              className="w-full z-0 h-6  absolute  left-0 -bottom-6 "
              style={{
                backgroundColor:
                  theme === 'LIGHT' ? 'rgba(31, 31, 31, 0.03)' : 'rgba(255, 255, 255, 0.1)',
              }}
            >
              <div className="absolute bottom-0 -left-6 size-6 overflow-hidden">
                <style>{`
      .curved-corner-bottomright {
        width: 100%;
        height: 100%;
        overflow: hidden;
        position: relative;
      }
      .curved-corner-bottomright:before {
        content: "";
        display: block;
        width: 200%;
        height: 200%;
        position: absolute;
        border-radius: 50%;
        bottom: 0;
        right: 0;
        box-shadow: 12px 12px 0 0 ${theme === 'LIGHT' ? 'rgba(31, 31, 31, 0.03)' : 'rgba(255, 255, 255, 0.1)'};
      }
    `}</style>
                <div className="curved-corner-bottomright"></div>
              </div>
            </div>
          </div>
        </div>

        <div
          className={`w-full min-h-[516px] rounded-[20px] p-4 mt-6`}
          style={{
            borderRadius: '20px 0 20px 20px',
            backgroundColor:
              theme === 'LIGHT' ? 'rgba(31, 31, 31, 0.03)' : 'rgba(255, 255, 255, 0.1)',
          }}
        >
          <div className="flex flex-col   gap-4 w-full">
            <h3
              className="text-xl font-medium"
              style={{
                color: theme === 'DARK' ? 'white' : 'black',
                fontFamily: visualIdentity?.fontFamily || 'Poppins',
              }}
            >
              {form.watch('name') || t('waitingRoom.previewPlaceholders.quizTitle')}
            </h3>

            <p
              className="text-sm"
              style={{
                fontFamily: visualIdentity?.fontFamily || 'Poppins',
                color: theme === 'DARK' ? 'white' : '#6D6E7A',
              }}
            >
              {form.watch('subtitle') || t('waitingRoom.previewPlaceholders.quizSubtitle')}
            </p>

            <div className="flex flex-col gap-4">
              {questions.length > 0 && currentQuestion ? (
                <div>
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center">
                      {quizStep > 1 && (
                        <ArrowLeftIcon
                          className="size-6 text-[#9E9FA6] cursor-pointer mr-2"
                          onClick={() => {
                            setQuizStep(quizStep - 1);
                          }}
                        />
                      )}
                      <h3
                        className="text-base font-medium"
                        style={{
                          color: theme === 'DARK' ? 'white' : 'black',
                          fontFamily: visualIdentity?.fontFamily || 'Poppins',
                        }}
                      >
                        <span
                          style={{
                            color: theme === 'DARK' ? '#95B6FF' : '#2C6CFF',
                            fontFamily: visualIdentity?.fontFamily || 'Poppins',
                          }}
                        >
                          {quizStep}/{questions.length}
                        </span>{' '}
                        {currentQuestion.textContent || t('waitingRoom.survey.untitledQuestion')}
                      </h3>
                    </div>
                    {quizStep < questions.length && (
                      <ArrowRightIcon
                        className="size-6 text-[#9E9FA6] cursor-pointer"
                        onClick={() => {
                          setQuizStep(quizStep + 1);
                        }}
                      />
                    )}
                  </div>
                  <div className="flex flex-col gap-2 pt-4">
                    <RadioGroup
                      value={selectedAnswer[quizStep - 1]?.toString() || ''}
                      onValueChange={(value) => {
                        setSelectedAnswer((prev) => ({
                          ...prev,
                          [quizStep - 1]: parseInt(value, 10),
                        }));
                      }}
                      className="space-y-2"
                    >
                      {currentQuestion.options.map((option, optionIndex) => (
                        <div
                          key={optionIndex}
                          className="flex items-center gap-2 p-2 rounded-lg"
                          style={{
                            backgroundColor:
                              theme === 'LIGHT'
                                ? 'rgba(31, 31, 31, 0.03)'
                                : 'rgba(255, 255, 255, 0.1)',
                          }}
                        >
                          <RadioGroupItem
                            value={optionIndex.toString()}
                            id={`quiz-${quizStep - 1}-${optionIndex}`}
                          />
                          <label
                            htmlFor={`quiz-${quizStep - 1}-${optionIndex}`}
                            className="text-base cursor-pointer flex-1"
                            style={{
                              color: theme === 'DARK' ? 'white' : 'black',
                              fontFamily: visualIdentity?.fontFamily || 'Poppins',
                            }}
                          >
                            {option.textContent || t('waitingRoom.survey.emptyAnswer')}
                          </label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p
                    className="text-sm"
                    style={{
                      color: theme === 'DARK' ? '#6D6E7A' : '#6D6E7A',
                      fontFamily: visualIdentity?.fontFamily || 'Poppins',
                    }}
                  >
                    {t('waitingRoom.survey.noQuestions')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
